import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, Platform, Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { api, apiPost, apiDelete, getToken, getCsrfToken } from '../../src/api/client';
import { config } from '../../src/config';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import Input from '../../src/components/Input';
import Button from '../../src/components/Button';
import Card from '../../src/components/Card';
import InlineConfirm from '../../src/components/InlineConfirm';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';

interface ProductImage {
  id: string;
  imageUrl: string;
  sortOrder: number;
}

interface Product {
  id: string;
  name: string;
  sku: string | null;
  description: string | null;
  priceInCents: number;
  imageUrl: string | null;
  isActive: boolean;
  images: ProductImage[];
  createdAt: string;
}

// /uploads/ is served by the API host (api.rinsepay.{design,app}),
// NOT by the frontend host — there's no /uploads location block in
// the frontend nginx vhost.  So image srcs need to point at the API
// origin on every platform.  config.apiUrl flips between
// api.rinsepay.design (dev builds) and api.rinsepay.app (release
// builds) automatically via the APP_ENV-driven .env switch.
const API_BASE = config.apiUrl;

export default function ProductsPage() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const data = await api<Product[]>('/api/admin/products');
      if (!signal?.aborted) setProducts(data);
    } catch {
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchData(ac.signal);
    return () => ac.abort();
  }, [fetchData]);

  const resetForm = () => {
    setName(''); setSku(''); setPrice(''); setDescription('');
    setEditingId(null); setShowForm(false);
  };

  const handleEdit = (p: Product) => {
    setName(p.name);
    setSku(p.sku || '');
    setPrice((p.priceInCents / 100).toFixed(2));
    setDescription(p.description || '');
    setEditingId(p.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    const priceCents = Math.round(parseFloat(price) * 100);
    if (!priceCents || priceCents <= 0) return;

    try {
      setSubmitting(true);
      const body = { name: name.trim(), sku: sku.trim() || undefined, price: priceCents, description: description.trim() || undefined };
      if (editingId) {
        await api(`/api/admin/products/${editingId}`, { method: 'PUT', body });
      } else {
        await apiPost('/api/admin/products', body);
      }
      resetForm();
      fetchData();
    } catch {
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setDeleting(true);
      await apiDelete(`/api/admin/products/${id}`);
      setConfirmDeleteId(null);
      fetchData();
    } catch {
    } finally {
      setDeleting(false);
    }
  };

  /* Upload multipart formData to the product images endpoint, mirroring the
   * auth pattern in admin/firmware.tsx — web sends CSRF + cookie, native
   * sends Bearer.  Mirrors api()'s refresh-on-401 retry: the access
   * cookie has a 15-min TTL, so on a long-open admin tab the first
   * upload after >15 min would otherwise 401.  We can't go through
   * api() itself because it stringifies the body as JSON; multipart
   * has to use raw fetch. */
  const postImage = async (productId: string, formData: FormData) => {
    const IS_WEB = Platform.OS === 'web';
    const buildHeaders = (): Record<string, string> => {
      const h: Record<string, string> = {};
      if (IS_WEB) {
        h['X-Client-Type'] = 'web';
        const csrf = getCsrfToken();
        if (csrf) h['X-CSRF-Token'] = csrf;
      } else {
        const token = getToken();
        if (token) h['Authorization'] = `Bearer ${token}`;
      }
      return h;
    };
    const url = `${config.apiUrl}/api/admin/products/${productId}/images`;
    const doPost = () => fetch(url, {
      method: 'POST',
      headers: buildHeaders(),
      body: formData,
      ...(IS_WEB ? { credentials: 'include' as RequestCredentials } : {}),
    });

    let resp = await doPost();

    // 401 → refresh once and retry, same as api() in src/api/client.ts
    if (resp.status === 401 && IS_WEB && getCsrfToken()) {
      const refreshResp = await fetch(`${config.apiUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client-Type': 'web' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (refreshResp.ok) {
        resp = await doPost();
      }
    }

    if (!resp.ok) {
      let msg = `Upload failed (${resp.status})`;
      try { const err = await resp.json(); if (err?.error) msg = err.error; } catch {}
      throw new Error(msg);
    }
  };

  const handleImageUpload = async (productId: string) => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        setUploadingFor(productId);
        try {
          const formData = new FormData();
          formData.append('file', file);
          await postImage(productId, formData);
          fetchData();
        } catch (err: any) {
          alert(err?.message || 'Upload failed');
        } finally {
          setUploadingFor(null);
        }
      };
      input.click();
      return;
    }
    // Native: use react-native-image-picker (Expo has been removed)
    try {
      const ImagePicker = await import('react-native-image-picker');
      const result = await ImagePicker.launchImageLibrary({
        mediaType: 'photo',
        quality: 0.8,
        selectionLimit: 1,
        includeBase64: false,
      });
      if (result.didCancel || !result.assets || result.assets.length === 0) return;
      setUploadingFor(productId);
      const asset = result.assets[0];
      const uri = asset.uri || '';
      const filename = uri.split('/').pop() || 'photo.jpg';
      const ext = filename.split('.').pop()?.toLowerCase() || 'jpg';
      const type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      const formData = new FormData();
      formData.append('file', { uri, name: filename, type } as any);
      await postImage(productId, formData);
      fetchData();
    } catch (err: any) {
      alert(err?.message || 'Upload failed');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    try {
      await apiDelete(`/api/admin/products/images/${imageId}`);
      fetchData();
    } catch (err: any) {
      alert(err?.message || 'Delete failed');
    }
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  if (loading && products.length === 0) {
    return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }

  return (
    <View style={styles.container}>
      <ContentContainer footer={false}>
        <View style={styles.header}>
          <Text style={styles.title}>Products</Text>
          {!showForm && (
            <TouchableOpacity onPress={() => setShowForm(true)} style={styles.addBtn}>
              <Ionicons name="add" size={20} color={colors.white} />
              <Text style={styles.addBtnText}>Add Product</Text>
            </TouchableOpacity>
          )}
        </View>

        {showForm && (
          <Card style={{ marginBottom: spacing.md }}>
            <Text style={styles.formTitle}>{editingId ? 'Edit Product' : 'New Product'}</Text>
            <Input label="Name *" value={name} onChangeText={setName} placeholder="Product name" />
            <Input label="SKU" value={sku} onChangeText={setSku} placeholder="e.g. RPC-100" />
            <Input label="Price ($) *" value={price} onChangeText={setPrice} placeholder="99.99" keyboardType="decimal-pad" />
            <Input label="Description" value={description} onChangeText={setDescription} placeholder="Product description" multiline maxLength={1000} />
            <View style={{ flexDirection: 'row', gap: spacing.md, marginTop: spacing.sm }}>
              <Button title="Cancel" onPress={resetForm} variant="outline" style={{ flex: 1 }} />
              <Button title={editingId ? 'Update' : 'Create'} onPress={handleSubmit} loading={submitting} style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        <FlatList ListFooterComponent={<PageFooter />}
          data={products}
          keyExtractor={p => p.id}
          renderItem={({ item: p }) => (
            <Card style={[styles.productCard, !p.isActive && { opacity: 0.5 }]}>
              <View style={styles.productHeader}>
                {p.imageUrl ? (
                  <Image source={{ uri: `${API_BASE}${p.imageUrl}` }} style={styles.thumbnail} />
                ) : (
                  <View style={[styles.thumbnail, { backgroundColor: colors.surfaceSecondary, justifyContent: 'center', alignItems: 'center' }]}>
                    <Ionicons name="image-outline" size={24} color={colors.textLight} />
                  </View>
                )}
                <View style={{ flex: 1, marginLeft: spacing.md }}>
                  <Text style={styles.productName}>{p.name}</Text>
                  {p.sku && <Text style={styles.productSku}>SKU: {p.sku}</Text>}
                  <Text style={styles.productPrice}>{fmt(p.priceInCents)}</Text>
                  {p.description && <Text style={styles.productDesc} numberOfLines={2}>{p.description}</Text>}
                </View>
                <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                  <TouchableOpacity onPress={() => handleEdit(p)} style={styles.iconBtn}>
                    <Ionicons name="pencil" size={18} color={colors.primary} />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setConfirmDeleteId(p.id)} style={styles.iconBtn}>
                    <Ionicons name="trash" size={18} color={colors.danger} />
                  </TouchableOpacity>
                </View>
              </View>

              <InlineConfirm
                message={`Deactivate "${p.name}"?`}
                visible={confirmDeleteId === p.id}
                onConfirm={() => handleDelete(p.id)}
                onCancel={() => setConfirmDeleteId(null)}
                confirmLabel="Deactivate"
                loading={deleting}
                variant="danger"
              />

              {/* Images */}
              <View style={styles.imagesRow}>
                {p.images.map(img => (
                  <View key={img.id} style={styles.imageWrap}>
                    <Image source={{ uri: `${API_BASE}${img.imageUrl}` }} style={styles.imageThumb} />
                    <TouchableOpacity onPress={() => handleDeleteImage(img.id)} style={styles.imageDeleteBtn}>
                      <Ionicons name="close-circle" size={18} color={colors.danger} />
                    </TouchableOpacity>
                  </View>
                ))}
                {p.images.length < 4 && (
                  <TouchableOpacity onPress={() => handleImageUpload(p.id)} style={[styles.imageWrap, styles.imageAddBtn]}>
                    {uploadingFor === p.id ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Ionicons name="add" size={24} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                )}
              </View>
            </Card>
          )}
          ListEmptyComponent={
            !showForm ? (
              <Card style={{ alignItems: 'center', paddingVertical: spacing.xxl }}>
                <Ionicons name="pricetag-outline" size={48} color={colors.textLight} />
                <Text style={[styles.productName, { marginTop: spacing.md }]}>No products yet</Text>
              </Card>
            ) : null
          }
        />
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: c.primary, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md },
  addBtnText: { color: c.white, fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  formTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  productCard: { marginBottom: spacing.md },
  productHeader: { flexDirection: 'row', alignItems: 'flex-start' },
  thumbnail: { width: 60, height: 60, borderRadius: borderRadius.md },
  productName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  productSku: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  productPrice: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.primary, marginTop: 2 },
  productDesc: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 4 },
  iconBtn: { padding: spacing.xs },
  imagesRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.md, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: c.borderLight },
  imageWrap: { position: 'relative' },
  imageThumb: { width: 64, height: 64, borderRadius: borderRadius.sm },
  imageDeleteBtn: { position: 'absolute', top: -6, right: -6 },
  imageAddBtn: { width: 64, height: 64, borderRadius: borderRadius.sm, borderWidth: 1, borderStyle: 'dashed', borderColor: c.borderLight, justifyContent: 'center', alignItems: 'center' },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
